import { getReportFooterText } from '../reportFooter';

export interface PropertyBookListingRow {
  location: string;
  lin: string | null;
  nsn: string | null;
  size?: string | null;
  nomenclature: string;
  unitOfIssue?: string | null;
  arc?: string | null;
  onHand: number;
  unitPrice: number | null;
  totalPrice: number | null;
}

export interface PropertyBookListingNsnGroup {
  nsn: string;
  rows: PropertyBookListingRow[];
  /** Unit price x on-hand quantity, summed across all locations for this NSN in the filtered result set. */
  aslValue: number | null;
}

export interface PropertyBookListingTemplateOptions {
  generatedAt: Date;
  linFilter?: string | null;
  groups: PropertyBookListingNsnGroup[];
}

const ARC_TOOLTIP =
  'D = Durable item; N = Non-expendable (never used up); X = Expendable (will be used up)';

const ARC_LABELS: Record<string, string> = {
  D: 'Durable item',
  N: 'Non-expendable (never used up)',
  X: 'Expendable (will be used up)',
};

const ARC_COLORS: Record<string, string> = {
  D: '#1976D2',
  N: '#7B1FA2',
  X: '#D32F2F',
};

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const sanitizeText = (value: string | null | undefined): string => {
  const trimmed = value?.toString().trim();
  if (!trimmed) {
    return '—';
  }
  return escapeHtml(trimmed);
};

const formatCurrency = (value: number | null | undefined): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '—';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(value);
};

const formatDateTime = (value: Date): string =>
  new Intl.DateTimeFormat('en-US', {
    dateStyle: 'long',
    timeStyle: 'short',
  }).format(value);

/** Renders the ARC value as a colored indicator badge rather than plain text. */
const buildArcBadge = (value: string | null | undefined): string => {
  const code = value?.trim().toUpperCase();
  if (!code) {
    return '—';
  }
  const color = ARC_COLORS[code] ?? '#5c6f85';
  const title = ARC_LABELS[code] ?? code;
  return `<span class="arc-badge" style="background:${color}" title="${escapeHtml(title)}">${escapeHtml(code)}</span>`;
};

const buildGroupRows = (group: PropertyBookListingNsnGroup): string => {
  const detailRows = group.rows
    .map(
      (row) => `
        <tr>
          <td>${sanitizeText(row.location)}</td>
          <td>${sanitizeText(row.lin)}</td>
          <td>${sanitizeText(row.nsn)}</td>
          <td>${sanitizeText(row.size)}</td>
          <td>${sanitizeText(row.nomenclature)}</td>
          <td>${sanitizeText(row.unitOfIssue)}</td>
          <td class="arc-cell">${buildArcBadge(row.arc)}</td>
          <td class="numeric">${row.onHand}</td>
          <td class="numeric">${formatCurrency(row.unitPrice)}</td>
          <td class="numeric">${formatCurrency(row.totalPrice)}</td>
          <td class="numeric">—</td>
        </tr>
      `,
    )
    .join('');

  return `
    ${detailRows}
    <tr class="asl-row">
      <td colspan="10">ASL Value — NSN ${sanitizeText(group.nsn)}</td>
      <td class="numeric">${formatCurrency(group.aslValue)}</td>
    </tr>
  `;
};

const buildTableBody = (groups: PropertyBookListingNsnGroup[]): string => {
  if (groups.length === 0) {
    return `
      <tr>
        <td colspan="11" class="empty-row">No property book items match the selected filters.</td>
      </tr>
    `;
  }

  return groups.map(buildGroupRows).join('');
};

export const buildPropertyBookListingReportHtml = ({
  generatedAt,
  linFilter,
  groups,
}: PropertyBookListingTemplateOptions): string => {
  const generatedLabel = formatDateTime(generatedAt);
  const linFilterLabel = linFilter?.trim() ? sanitizeText(linFilter) : 'All LINs';
  const tableBody = buildTableBody(groups);

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>Property Book Listing</title>
    <style>
      :root {
        color-scheme: light;
      }

      * {
        box-sizing: border-box;
      }

      body {
        margin: 16px;
        font-family: 'Roboto', 'Helvetica Neue', Arial, sans-serif;
        color: #15263c;
        background: #ffffff;
      }

      header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 24px;
        margin-bottom: 24px;
      }

      .brand {
        font-size: 14px;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: #3a82f6;
        font-weight: 600;
      }

      .title {
        font-size: 28px;
        font-weight: 700;
        margin: 0;
      }

      .generated {
        margin-top: 4px;
        font-size: 14px;
        color: #4a5c72;
      }

      .meta-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
        gap: 16px;
        margin-bottom: 24px;
      }

      .meta-block {
        padding: 12px 16px;
        border: 1px solid #e1e8f5;
        border-radius: 8px;
        background: #f8fbff;
      }

      .meta-label {
        display: block;
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: #5c6f85;
        margin-bottom: 4px;
      }

      .meta-value {
        font-size: 16px;
        font-weight: 600;
        color: #15263c;
      }

      table {
        width: 100%;
        table-layout: fixed;
        border-collapse: collapse;
        border: 1px solid #d7e3f4;
      }

      thead th {
        background: #e9f1ff;
        text-align: left;
        font-size: 12px;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        padding: 10px 12px;
        color: #4a5c72;
        border-bottom: 1px solid #d7e3f4;
      }

      thead th.numeric {
        text-align: center;
      }

      tbody td {
        padding: 12px;
        border-bottom: 1px solid #edf3fc;
        font-size: 14px;
        color: #1f2f45;
      }

      tbody tr:last-child td {
        border-bottom: none;
      }

      .numeric {
        text-align: center;
        font-variant-numeric: tabular-nums;
      }

      .arc-cell {
        text-align: center;
      }

      .arc-badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 22px;
        height: 22px;
        border-radius: 50%;
        color: #ffffff;
        font-size: 11px;
        font-weight: 700;
        line-height: 1;
      }

      .asl-row td {
        background: #eef6ec;
        font-weight: 700;
      }

      .empty-row {
        text-align: center;
        font-style: italic;
        color: #6b7c92;
        padding: 24px 12px;
      }

      footer {
        margin-top: 32px;
        font-size: 12px;
        color: #5c6f85;
        text-align: center;
      }

      @media print {
        body {
          margin: 8mm;
        }

        @page {
          size: landscape;
        }

        table {
          table-layout: auto;
        }

        thead th,
        tbody td {
          font-size: 10px;
          padding: 4px 6px;
        }
      }
    </style>
  </head>
  <body>
    <header>
      <div>
        <div class="brand">eQuip Transaction Suite</div>
        <h1 class="title">Property Book Listing</h1>
        <div class="generated">Generated ${escapeHtml(generatedLabel)}</div>
      </div>
      <div class="meta-grid">
        <div class="meta-block">
          <span class="meta-label">LIN Filter</span>
          <span class="meta-value">${linFilterLabel}</span>
        </div>
      </div>
    </header>

    <table data-sheet-name="Property Book Listing">
      <thead>
        <tr>
          <th>Location</th>
          <th>LIN</th>
          <th>NSN</th>
          <th>Size</th>
          <th>Nomenclature</th>
          <th>UI</th>
          <th title="${ARC_TOOLTIP}">ARC ⓘ</th>
          <th class="numeric">On Hand</th>
          <th class="numeric">Unit Price</th>
          <th class="numeric">Total Price</th>
          <th class="numeric">ASL Value</th>
        </tr>
      </thead>
      <tbody>
        ${tableBody}
      </tbody>
    </table>

    <footer>
      Generated ${escapeHtml(generatedLabel)} · ${getReportFooterText()}
    </footer>
  </body>
</html>`;
};
